import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Check, X, Pencil } from 'lucide-react';
import styles from './InlineEdit.module.scss';

/* ────────────────────────────────────────────────────────────
 * InlineEdit — click-to-edit value, with commit / cancel.
 *
 * Default usage:
 *
 *   <InlineEdit
 *     value={item.name}
 *     onCommit={(v) => save(item.id, v)}
 *     placeholder="Untitled"
 *   />
 *
 * Keyboard: Enter commits, Escape cancels. Loses focus → commits
 * (configurable via `commitOnBlur={false}`). Validate via
 * `validate(value)` → string error message or empty.
 *
 * For grid / spreadsheet contexts, use <EditableCell> (companion
 * component below) which strips chrome and lays out flush with
 * a cell — that's the spec referenced in SPREADSHEET_INLINE_EDIT.md.
 * ──────────────────────────────────────────────────────────── */

const InlineEdit = ({
  value,
  onCommit,
  onCancel,
  placeholder,
  type,
  validate,
  disabled,
  commitOnBlur,
  saving,
  formatDisplay,
  size,
  className,
  ariaLabel,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select the text on entering edit mode so typing replaces it —
      // matches spreadsheet UX expectations.
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = useCallback(() => {
    if (validate) {
      const message = validate(draft);
      if (message) { setError(message); return; }
    }
    setError('');
    if (draft !== value) onCommit(draft);
    setEditing(false);
  }, [draft, value, validate, onCommit]);

  const cancel = useCallback(() => {
    setDraft(value);
    setError('');
    setEditing(false);
    if (onCancel) onCancel();
  }, [value, onCancel]);

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const onBlur = () => {
    if (!commitOnBlur) return;
    commit();
  };

  const dirty = draft !== value;
  const display = formatDisplay ? formatDisplay(value) : value;

  const rootCls = [
    styles.root,
    size === 'sm' ? styles.sizeSm : '',
    size === 'lg' ? styles.sizeLg : '',
    disabled ? styles.disabled : '',
    editing ? styles.editing : '',
    error ? styles.errored : '',
    dirty && editing ? styles.dirty : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!editing) {
    return (
      <button
        type="button"
        className={rootCls}
        disabled={disabled}
        onClick={() => !disabled && setEditing(true)}
        aria-label={ariaLabel || 'Edit value'}
      >
        <span className={styles.display}>
          {display || <span className={styles.placeholder}>{placeholder}</span>}
        </span>
        <span className={styles.pencil} aria-hidden="true">
          <Pencil size={14} strokeWidth={1.5} />
        </span>
      </button>
    );
  }

  return (
    <span className={rootCls}>
      <input
        ref={inputRef}
        className={styles.input}
        type={type}
        value={draft ?? ''}
        placeholder={placeholder}
        disabled={disabled || saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        aria-label={ariaLabel || 'Edit value'}
      />
      <span className={styles.controls}>
        <button
          type="button"
          className={[styles.iconBtn, styles.confirm].join(' ')}
          onClick={commit}
          disabled={saving || Boolean(error)}
          aria-label="Save"
        >
          <Check size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          className={[styles.iconBtn, styles.cancel].join(' ')}
          onClick={cancel}
          disabled={saving}
          aria-label="Cancel"
        >
          <X size={14} strokeWidth={2} />
        </button>
      </span>
      {error && <span className={styles.error}>{error}</span>}
    </span>
  );
};

InlineEdit.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  validate: PropTypes.func,
  disabled: PropTypes.bool,
  commitOnBlur: PropTypes.bool,
  saving: PropTypes.bool,
  formatDisplay: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

InlineEdit.defaultProps = {
  value: '',
  onCancel: null,
  placeholder: '—',
  type: 'text',
  validate: null,
  disabled: false,
  commitOnBlur: true,
  saving: false,
  formatDisplay: null,
  size: 'md',
  className: '',
  ariaLabel: '',
};

export default InlineEdit;
